import React, { useState, useRef, useEffect } from 'react';
import { Form, InputGroup, Button } from 'react-bootstrap';
import styles from './SearchableSelect.module.css';

/**
 * A searchable select component that combines a dropdown with search functionality
 * 
 * @param {Object} props
 * @param {Array} props.options - Array of options with { code, name } structure
 * @param {string} props.value - Currently selected value
 * @param {function} props.onChange - Callback when selection changes
 * @param {string} props.size - Size of the component (sm, lg)
 * @param {string} props.placeholder - Placeholder text for search input
 * @returns {JSX.Element}
 */
const SearchableSelect = ({
  options = [],
  value = '',
  onChange,
  size = 'sm',
  placeholder = 'Search languages...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get display name for selected value
  const selectedName = options.find(opt => opt.code === value)?.name || '';

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Handle option selection
  const handleSelect = (code) => {
    onChange(code);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  return (
    <div ref={dropdownRef} className={styles.dropdownContainer}>
      <Button
        variant="outline-secondary"
        size={size}
        onClick={() => setIsOpen(!isOpen)}
        className={styles.dropdownButton}
      >
        <span className="text-truncate">
          {selectedName || 'Select language'}
        </span>
        <span className="ms-2">▼</span>
      </Button>

      {isOpen && (
        <div className={styles.dropdownMenu}>
          <div className={styles.searchContainer}>
            <InputGroup size={size}>
              <Form.Control
                ref={searchInputRef}
                size={size}
                type="text"
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  size={size}
                  onClick={() => setSearchTerm('')}
                >
                  ×
                </Button>
              )}
            </InputGroup>
          </div>

          <div className={styles.optionsList}>
            {filteredOptions.length === 0 ? (
              <div className={styles.noResults}>No matches found</div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.code}
                  className={`${styles.optionItem} ${
                    option.code === value ? styles.selectedOption : ''
                  }`}
                  onClick={() => handleSelect(option.code)}
                >
                  {option.name}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
